import { Building2, ClipboardCheck, ClipboardPen, LayoutDashboard, Monitor, Package, Ticket, UsersRound } from 'lucide-react'
import { AppPaths } from '@/config/app-paths'
import type { StaffNavContext } from '../staff-nav-context'
import { group, leaf, pushIfPresent, type StaffNavNode } from '../nav-node'
import { buildAccountGroup } from './account'

/**
 * Owner shell for a plain Administrator (not SystemAdministrator).
 * In-house staff and their attendance, plus user roles.
 * Dashboard and payrolls are parked until that overview is ready.
 * Gate pass approval stays with the assigned supervisor and is also listed here
 * so the owner admin can approve requests onboarding has passed on.
 */
export function buildThinAdminTree(ctx: StaffNavContext): StaffNavNode[] {
  const children: StaffNavNode[] = [
    leaf(
      'in_house_staff',
      'In-house staff',
      AppPaths.workforceDirectory,
      Building2,
      ['in-house', 'office', 'employee', 'documents', 'mazhar', 'amglobal'],
      { activeWhenPrefix: true },
    ),
    leaf(
      'in_house_attendance',
      'In-house attendance',
      AppPaths.inHouseAttendance,
      ClipboardPen,
      ['attendance', 'present', 'absent', 'in-house', 'mark'],
      { activeWhenPrefix: false },
    ),
    leaf(
      'boss_attendance',
      'Attendance summary',
      AppPaths.bossAttendance,
      ClipboardCheck,
      ['attendance', 'present', 'absent', 'today'],
      { activeWhenPrefix: false },
    ),
    leaf(
      'admin_users',
      'Users and roles',
      AppPaths.adminTeam,
      UsersRound,
      ['team', 'users', 'roles', 'staff', 'access', 'invite'],
      { activeWhenPrefix: true },
    ),
  ]

  children.push(
    leaf(
      'admin_gate_pass',
      'Gate Pass Approvals',
      AppPaths.adminGatePasses,
      Ticket,
      ['gate', 'pass', 'approval', 'approve', 'onboarding'],
      { activeWhenPrefix: true },
    ),
  )

  if (ctx.showDesktopActivityNav) {
    children.push(
      leaf(
        'desktop_activity',
        'Desktop Activity',
        AppPaths.adminDesktopActivity,
        Monitor,
        ['desktop', 'activity', 'screenshots', 'enrollment', 'tracker'],
        { activeWhenPrefix: true },
      ),
    )
  }

  if (ctx.showMyDesktopActivityNav) {
    children.push(
      leaf(
        'my_desktop_activity',
        'My activity',
        AppPaths.myDesktopActivity,
        Monitor,
        ['desktop', 'activity', 'screenshots', 'timer', 'progress'],
        { activeWhenPrefix: true },
      ),
    )
  }

  if (ctx.showStocksNav) {
    children.push(
      leaf(
        'stocks',
        'Stocks',
        AppPaths.adminStocks,
        Package,
        ['stocks', 'inventory', 'store', 'materials'],
        { activeWhenPrefix: true },
      ),
    )
  }

  const tree: StaffNavNode[] = []
  pushIfPresent(
    tree,
    group('overview', 'Overview', LayoutDashboard, ['overview', 'in-house', 'attendance'], children),
  )
  pushIfPresent(tree, ctx.showAccountSection ? buildAccountGroup(ctx) : null)
  return tree
}
